import React, { useState } from "react";
import { Pagination, Tabs } from "antd";
import { FiChevronRight } from "react-icons/fi";
import { useHistory } from "react-router-dom";
import { useBrandPaymentHistory } from "../../hooks/usePayment";
import { formatCurrency, parseApiAmount } from "../../utils/amountFormat";
import {
  brandPaymentStatusClass,
  brandPaymentStatusLabel,
} from "../../utils/brandPaymentStatus";
import { getPaymentCardBrandLabel } from "../../utils/paymentCardBrand";
import { getPaymentMethodLabel } from "../../utils/paymentMethod";

const TABS = [
  { key: "all", label: "전체" },
  { key: "paid", label: "결제완료" },
  { key: "pending", label: "결제 요청 중" },
  { key: "cancelled", label: "환불신청" },
  { key: "refunded", label: "환불완료" },
];

export const BrandPaymentHistory = () => {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState("all");

  const [campaign_id] = useState(null);
  const status = activeTab === "all" ? null : activeTab;

  const [page, setPage] = useState(1);

  const { data, isLoading } = useBrandPaymentHistory({
    campaignId: campaign_id,
    status,
    page,
  });

  const total = data?.total;
  const list = Array.isArray(data?.result) ? data?.result : [];

  const handleOpenDetail = (item) => {
    history.push({
      pathname: `/payments/brand-payments-history/detail/${item.id}`,
      state: item,
    });
  };

  const tabItems = TABS.map((tab) => ({
    key: tab.key,
    label: tab.label,
  }));

  return (
    <div className="w-full max-w-[1400px]">
      <h2 className="md:text-4xl text-3xl font-bold text-[#111111]">
        결제 내역
      </h2>

      <Tabs
        activeKey={activeTab}
        items={tabItems}
        onChange={setActiveTab}
        className="w-full font-normal mb-5"
      />

      <div className="space-y-4">
        {list.length === 0 && !isLoading && (
          <div className="rounded-[22px] border border-dashed border-[#e7eaee] bg-[#fafbfc] px-6 py-12 text-center text-[15px] text-[#7a7f87]">
            결제 내역이 없습니다.
          </div>
        )}

        {list.map((item) => {
          const settlementAmount = parseApiAmount(item.paid_amount);
          const cardBrandLabel = getPaymentCardBrandLabel(item.card_brand);
          const thumb = item.campaign_thumbnail;
          const subLine =
            item.payer_name ||
            [cardBrandLabel, item.card_last4 && `끝자리 ${item.card_last4}`]
              .filter(Boolean)
              .join(" · ") ||
            "—";

          return (
            <button
              key={item.id}
              type="button"
              onClick={() => handleOpenDetail(item)}
              className="group w-full rounded-[22px] border border-[#e7eaee] bg-white text-left shadow-[0_10px_30px_rgba(17,24,39,0.04)] transition-all duration-200 hover:-translate-y-0.5 hover:border-[#cfd7e2] hover:shadow-[0_18px_44px_rgba(17,24,39,0.08)]"
            >
              <div className="flex flex-col gap-4 p-5 lg:flex-row lg:items-center lg:gap-5 lg:px-6 lg:py-4">
                <div className="shrink-0">
                  {thumb ? (
                    <div className="h-[100px] w-full overflow-hidden rounded-2xl bg-[#f1f5f9] sm:h-[120px] sm:w-[160px]">
                      <img
                        src={thumb}
                        alt=""
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    </div>
                  ) : (
                    <div className="flex h-[100px] w-full items-center justify-center rounded-2xl bg-gradient-to-br from-[#e8eef5] to-[#f1f5f9] sm:h-[120px] sm:w-[160px]">
                      <span className="text-[12px] font-medium text-[#94a3b8]">
                        썸네일 없음
                      </span>
                    </div>
                  )}
                </div>

                <div className="min-w-0 w-full flex-1 lg:min-w-[12rem]">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex rounded-[8px] px-2.5 py-1 text-[12px] font-bold ${brandPaymentStatusClass(
                        item.status
                      )}`}
                    >
                      {brandPaymentStatusLabel(item.status)}
                    </span>
                    {item.status === "paid" && item.cancelled_at && (
                      <span
                        className={`inline-flex rounded-[8px] px-2.5 py-1 text-[12px] font-bold bg-[#f1f5f9] text-[#64748b]`}
                      >
                        환불 거절
                      </span>
                    )}
                  </div>

                  <h3 className="mt-2.5 break-keep text-[17px] font-semibold leading-[1.4] tracking-[-0.03em] text-[#3f4650] md:text-[19px]">
                    {item.campaign_title}
                  </h3>

                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-[#8a929d]">
                    {item.paid_at ? <span>결제일 {item.paid_at}</span> : null}
                    {item.merchant_uid ? (
                      <span>
                        결제 번호{" "}
                        <span className="font-semibold text-[#5b616a]">
                          {String(item.merchant_uid).slice(4)}
                        </span>
                      </span>
                    ) : null}
                  </div>

                  <div className="mt-2.5 flex flex-wrap items-center gap-2 text-[13px] text-[#7a7f87]">
                    <span>결제 정보</span>
                    <span className="font-semibold text-[#343a40]">
                      {subLine}
                    </span>
                    {item.pay_method ? (
                      <span className="rounded-full bg-[#f1f5f9] px-2.5 py-1 text-[12px] font-semibold text-[#64748b]">
                        {getPaymentMethodLabel(item.pay_method)}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="flex w-full items-center justify-between gap-4 border-t border-[#edf0f3] pt-4 lg:w-auto lg:flex-none lg:justify-end lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
                  <div className="min-w-0 flex-1 text-left lg:w-[220px] lg:flex-none lg:text-right">
                    <div className="flex items-center gap-1 text-[#9aa1aa] lg:justify-end">
                      <span className="text-[12px] font-medium leading-snug">
                        결제 금액
                      </span>
                    </div>
                    <span className="mt-2.5 block text-[18px] font-bold tabular-nums tracking-[-0.04em] text-[#111827] sm:text-[20px]">
                      {formatCurrency(settlementAmount, item.currency)}
                    </span>
                  </div>
                  <FiChevronRight
                    className="h-[18px] w-[18px] shrink-0 text-[#94a3b8] transition-transform group-hover:translate-x-0.5"
                    aria-hidden
                  />
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <Pagination
        current={page}
        total={total}
        pageSize={20}
        onChange={(page) => setPage(page)}
        showSizeChanger={false}
        className="mt-12 justify-center"
      />
    </div>
  );
};
